/**
 * The re-read of the thread, and what hangs off it finishing.
 *
 * A re-read is a refetch, and Apollo rejects a refetch on a network error — and with
 * an AbortError when a newer refetch supersedes it, which happens routinely when a
 * turn ends right after a reconnect. Neither says anything about the work the caller
 * is doing, so neither may escape: a rejection that reached `retireAfterRead` would
 * strand the turn in "settling", and one that reached a form action would turn a
 * submit that already committed into an error.
 */
import type { AgentTurnStore } from "./turnStore";

/** Run the re-reads together; resolves once every one has finished, however it went. */
export const reconcileAll = async (
  reads: Array<() => Promise<unknown>>,
): Promise<void> => {
  await Promise.allSettled(reads.map((read) => read()));
};

/**
 * Retire the turn `runId` once the re-read has finished, whether or not it succeeded.
 *
 * Only that turn, and only once: a newer turn that has started since must not be
 * retired by this one's read. `SETTLED` itself is a no-op unless the turn is
 * "settling", so a failed turn stays on screen until it is dismissed.
 */
export const retireAfterRead = async (
  read: () => Promise<unknown>,
  store: AgentTurnStore,
  runId: string | null,
): Promise<void> => {
  await read().catch(() => undefined);
  if (store.getState().runId === runId) store.dispatch({ kind: "SETTLED" });
};
